/**
 * AdjustPointsPanel — en forelders manuelle bonus eller korrigering, inne i
 * barnekortet (#159, redesign steg 9a; var AdjustPointsSheet).
 *
 * Stell_Familie legger justeringen i selve kortet i stedet for et ark:
 * hurtigchips ±5/10/25, begrunnelsen, og én knapp som sier hva som skjer
 * («Gi Bella 10 p» / «Trekk 10 p fra Bella»). Tallfeltet står under chipsene
 * for andre beløp, så en forelder fortsatt kan rette med 3 eller 120.
 *
 * Begrunnelsen er påkrevd, og etiketten sier hvorfor: barnet ser raden i sin
 * egen lommebokhistorikk, og en uforklart ±N der er verre enn ingen rad. Serveren
 * håndhever samme regel (ikke-tom merknad, |beløp| ≤ 500), så dette er den
 * vennlige halvdelen av en sperre som holder uansett.
 *
 * Fortegnet er en del av beløpet, ikke en egen bryter, så tallet forelderen
 * leser er nøyaktig det som havner i hovedboka.
 *
 * Utløpt adminopplåsing som ellers i konsollen: en 403 åpner AdminReauthModal
 * og prøver lagringen på nytt etterpå. En feil holder panelet åpent med
 * feltene urørt og en feilmelding i panelet.
 */
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, SheetError } from '../../components/primitives';
import { AdminReauthModal } from '../admin/AdminReauthModal';
import { ApiError, isAdminScopeError } from '../admin/useMembers';
import { useAdjustPoints } from './useInstanceControl';

/** Skissens rekkefølge: trekk til venstre, gi til høyre. */
const QUICK = [-25, -10, -5, 5, 10, 25] as const;
const MAX_ABS = 500;

interface AdjustPointsPanelProps {
  member: { id: string; short_name: string };
  onClose: () => void;
}

export function AdjustPointsPanel({ member, onClose }: AdjustPointsPanelProps): ReactElement {
  const { t } = useTranslation();
  const adjust = useAdjustPoints();
  const amountHintId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({
    open: false,
    retry: null,
  });

  // Juster-knappen forsvinner når panelet åpnes, så fokus flyttes hit i stedet
  // for å falle til <body>. Ikke til et felt: tastaturet skal ikke dukke opp.
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const parsedAmount = Number(amount);
  const amountValid =
    amount.trim() !== '' &&
    Number.isInteger(parsedAmount) &&
    parsedAmount !== 0 &&
    Math.abs(parsedAmount) <= MAX_ABS;
  const canSubmit = amountValid && note.trim().length > 0 && !adjust.isPending;

  const name = member.short_name;
  const submitLabel = !amountValid
    ? t('admin.adjust.submit')
    : parsedAmount > 0
      ? t('admin.adjust.give', { name, points: parsedAmount })
      : t('admin.adjust.take', { name, points: -parsedAmount });

  function submit(): void {
    if (!canSubmit) return;
    setError(null);
    const run = async (): Promise<void> => {
      try {
        await adjust.mutateAsync({
          memberId: member.id,
          amount: parsedAmount,
          note: note.trim(),
        });
        onClose();
      } catch (err) {
        if (isAdminScopeError(err)) {
          setReauth({ open: true, retry: () => void run() });
          return;
        }
        setError(err instanceof ApiError ? err.code : 'unknown');
      }
    };
    void run();
  }

  return (
    <>
      <div
        ref={panelRef}
        tabIndex={-1}
        role="group"
        aria-label={t('admin.adjust.title', { name })}
        data-testid="adjust-points-sheet"
        className="flex flex-col gap-3 outline-none"
      >
        <div
          role="group"
          aria-label={t('admin.adjust.quickLabel')}
          // Seks i bredden fra sm; på telefon blir 50 px-knappene for trange for
          // «−25», så trekk og gi står på hver sin rad.
          className="grid grid-cols-3 gap-1.5 sm:grid-cols-6"
        >
          {QUICK.map((q) => (
            <Chip
              key={q}
              selected={amountValid && parsedAmount === q}
              data-testid={q < 0 ? `adjust-quick-minus-${-q}` : `adjust-quick-plus-${q}`}
              onClick={() => setAmount(String(q))}
              className="w-full justify-center tnum"
            >
              {q < 0
                ? t('admin.adjust.quickSub', { amount: -q })
                : t('admin.adjust.quickAdd', { amount: q })}
            </Chip>
          ))}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-caption font-extrabold text-ink-2">{t('admin.adjust.amountLabel')}</span>
          <input
            type="number"
            inputMode="numeric"
            data-testid="adjust-amount"
            value={amount}
            min={-MAX_ABS}
            max={MAX_ABS}
            aria-describedby={amountHintId}
            onChange={(e) => setAmount(e.target.value)}
            className="field tnum"
          />
          <span id={amountHintId} className="text-caption text-ink-2">
            {t('admin.adjust.amountHint', { max: MAX_ABS })}
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-caption font-extrabold text-ink-2">
            {t('admin.adjust.noteLabel', { name })}
          </span>
          <input
            type="text"
            data-testid="adjust-note"
            maxLength={200}
            value={note}
            required
            placeholder={t('admin.adjust.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
            className="field"
          />
        </label>

        <SheetError message={error ? t('admin.adjust.error') : null} data-testid="adjust-error" />

        <Button
          size={52}
          onClick={submit}
          data-testid="adjust-submit"
          className="w-full"
          {...(canSubmit ? {} : { disabled: true })}
        >
          {submitLabel}
        </Button>
        <Button variant="ghost" onClick={onClose} data-testid="adjust-cancel" className="self-center">
          {t('common.cancel')}
        </Button>
      </div>

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={() => {
          const retry = reauth.retry;
          setReauth({ open: false, retry: null });
          retry?.();
        }}
      />
    </>
  );
}
